import { useEffect, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock, Phone } from "lucide-react";
import SEO from "../components/SEO";
import StatusBadge from "../components/StatusBadge";
import { FadeUp, MaskedLines, SectionLabel } from "../components/Reveal";
import { PHONE_DISPLAY, PHONE_TEL } from "../data/site";

export default function Article() {
    const { slug } = useParams();
    const [article, setArticle] = useState(undefined);
    const [others, setOthers] = useState([]);

    useEffect(() => {
        setArticle(undefined);
        fetch(`${process.env.PUBLIC_URL}/articles/${slug}.json`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setArticle)
            .catch(() => setArticle(null));
        fetch(`${process.env.PUBLIC_URL}/articles/index.json`)
            .then((r) => r.json())
            .then((list) => setOthers(list.filter((a) => a.slug !== slug)))
            .catch(() => setOthers([]));
    }, [slug]);

    if (article === undefined) return null;
    if (article === null) return <Navigate to="/blog" replace />;

    const schema = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: article.title,
        description: article.excerpt,
        image: article.img,
        author: { "@type": "Organization", name: "Serrurier Paris" },
        publisher: { "@type": "Organization", name: "Serrurier Paris" },
    };

    return (
        <div data-testid="article-page">
            <SEO title={`${article.title} | Blog Serrurier Paris`} description={article.excerpt} path={`/blog/${slug}`} schema={schema} />

            <section className="grain relative overflow-hidden bg-midnight py-10 text-white md:py-16">
                <div className="pointer-events-none absolute -left-24 top-0 h-80 w-80 rounded-full bg-emergency/15 blur-3xl" />
                <div className="relative mx-auto max-w-4xl px-4 sm:px-6">
                    <nav className="mb-8 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/50" data-testid="breadcrumb">
                        <Link to="/" className="transition-colors hover:text-gold">Accueil</Link>
                        <span>/</span>
                        <Link to="/blog" className="transition-colors hover:text-gold">Blog</Link>
                        <span>/</span>
                        <span className="text-gold">{article.category}</span>
                    </nav>
                    <SectionLabel dark>{article.category}</SectionLabel>
                    <StatusBadge className="mt-6" />
                    <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter sm:text-5xl">
                        <MaskedLines delay={0.15} lines={[article.title]} />
                    </h1>
                    <FadeUp delay={0.5}>
                        <div className="mt-6 flex items-center gap-4 text-sm text-white/60">
                            <span>{article.date}</span>
                            <span className="flex items-center gap-1.5">
                                <Clock className="h-4 w-4" /> {article.readTime} de lecture
                            </span>
                        </div>
                    </FadeUp>
                </div>
            </section>

            <FadeUp>
                <div className="mx-auto max-w-4xl px-4 sm:px-6">
                    <img
                        src={article.img}
                        alt={article.title}
                        className="-mt-2 aspect-[16/8] w-full rounded-xl object-cover shadow-[0_24px_60px_-20px_rgba(10,17,40,0.35)] md:-mt-8"
                        loading="lazy"
                        data-testid="article-cover"
                    />
                </div>
            </FadeUp>

            <article className="mx-auto max-w-3xl px-4 py-16 sm:px-6 md:py-20" data-testid="article-content">
                <FadeUp>
                    <p className="border-l-2 border-gold pl-6 font-display text-xl font-medium leading-relaxed text-midnight/85 md:text-2xl">
                        {article.excerpt}
                    </p>
                </FadeUp>
                {article.blocks.map((b, i) => (
                    <FadeUp key={b.h} delay={0.05}>
                        <section className="mt-12">
                            <h2 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">{b.h}</h2>
                            {b.ps.map((p, j) => (
                                <p key={j} className="mt-5 leading-relaxed text-midnight/70">{p}</p>
                            ))}
                        </section>
                    </FadeUp>
                ))}

                <FadeUp delay={0.1}>
                    <div className="grain mt-16 flex flex-wrap items-center justify-between gap-6 bg-midnight p-8 text-white md:p-10" data-testid="article-cta">
                        <div>
                            <h2 className="font-display text-2xl font-extrabold tracking-tight md:text-3xl">Besoin d'un serrurier maintenant ?</h2>
                            <p className="mt-2 text-sm text-white/60">Intervention en 30 minutes, 24h/24. Devis gratuit.</p>
                        </div>
                        <a
                            href={PHONE_TEL}
                            data-testid="article-call-button"
                            className="flex items-center gap-3 bg-emergency px-7 py-4 font-bold text-white transition-colors hover:bg-emergency-dark"
                        >
                            <Phone className="h-5 w-5" /> {PHONE_DISPLAY}
                        </a>
                    </div>
                </FadeUp>
            </article>

            <section className="mx-auto max-w-4xl px-4 pb-20 sm:px-6">
                <div className="flex items-center justify-between border-t border-midnight/10 pt-10">
                    <Link to="/blog" data-testid="article-back-link" className="flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-midnight/60 transition-colors hover:text-emergency">
                        <ArrowLeft className="h-4 w-4" /> Tous les articles
                    </Link>
                </div>
                <div className="mt-8 grid gap-5 md:grid-cols-2">
                    {others.map((o) => (
                        <Link
                            key={o.slug}
                            to={`/blog/${o.slug}`}
                            data-testid={`article-related-${o.slug}`}
                            className="group rounded-xl border border-midnight/10 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_50px_-20px_rgba(10,17,40,0.3)]"
                        >
                            <p className="text-xs font-bold uppercase tracking-wider text-emergency">{o.category}</p>
                            <h3 className="mt-2 font-display text-lg font-extrabold leading-snug tracking-tight">{o.title}</h3>
                            <span className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-emergency">
                                Lire <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
                            </span>
                        </Link>
                    ))}
                </div>
            </section>
        </div>
    );
}
